/**
 * Find2near — Map Provider Abstraction
 * ------------------------------------------------------------------
 * The goal is to make the map engine *replaceable* without rewriting
 * every page that uses a map. Pages should never import Leaflet /
 * MapLibre / Google directly — they import <Map /> from here.
 *
 * A provider is a plain object:
 *   {
 *     id:         "leaflet",              // unique key
 *     name:       "Leaflet + OSM",        // human-readable label
 *     available:  () => boolean,          // is it ready to render?
 *     Component:  React.ComponentType,    // the actual renderer
 *   }
 *
 * The Component receives the standardised MapProps below.
 *
 * Provider selection order (highest wins):
 *   1. `provider` prop on <Map />
 *   2. `localStorage.fn_map_provider`      (user override)
 *   3. `REACT_APP_MAP_PROVIDER` env var    (deployment default)
 *   4. First provider whose `available()` returns true
 *
 * Adding a new provider (MapLibre / Mappls / Google) later is:
 *   1. Write a component matching MapProps
 *   2. `registerMapProvider({...})` at module import time
 *   3. Done — no page changes required.
 *
 * MapProps contract (all optional except `center`):
 *   center:    [lat, lng]
 *   zoom:      number                     (default 12)
 *   height:    string | number            (default "100%")
 *   markers:   Array<{
 *     key?:    string,
 *     lat:     number,
 *     lng:     number,
 *     label?:  string,                    // short pin badge
 *     popup?:  ReactNode,                 // popup content
 *     pulse?:  boolean,                   // "me / live" pulse
 *     category?: string,                  // e.g. "hospital"
 *   }>
 *   tracking?: { provider_lat, provider_lng, customer_lat, customer_lng }
 *   onMarkerClick?: (marker) => void
 *   onMapClick?:    ({lat, lng}) => void
 *   controls?: {                          // provider-agnostic UI toggles
 *     layerSwitcher?: boolean,            // default true
 *     heatmap?:       boolean,            // density heat
 *   }
 */
import React from "react";

// Preserve a reference to the built-in Map constructor before we shadow it
// with our own `Map` component exported below (else `new Map()` will call the
// component and throw `Cannot read properties of undefined (reading 'provider')`).
const _NativeMap = globalThis.Map;
const _registry = new _NativeMap();

export function registerMapProvider(provider) {
  if (!provider || !provider.id || !provider.Component) return;
  _registry.set(provider.id, provider);
}

export function listMapProviders() {
  return Array.from(_registry.values());
}

export function getMapProvider(id) {
  return _registry.get(id) || null;
}

const STORAGE_KEY = "fn_map_provider";

export function resolveMapProviderId(explicit) {
  if (explicit && _registry.has(explicit)) return explicit;
  try {
    const saved = typeof window !== "undefined" && window.localStorage?.getItem(STORAGE_KEY);
    if (saved && _registry.has(saved)) return saved;
  } catch { /* localStorage disabled */ }
  const envPref = process.env.REACT_APP_MAP_PROVIDER;
  if (envPref && _registry.has(envPref)) return envPref;
  // Preferred default: modern MapLibre if it's registered, else the first
  // available (Leaflet fallback).
  if (_registry.has("maplibre")) return "maplibre";
  for (const p of _registry.values()) {
    try { if (!p.available || p.available()) return p.id; } catch { /* skip */ }
  }
  return null;
}

export function setPreferredMapProviderId(id) {
  try { window.localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
}

/**
 * <Map />: universal renderer. Pages should use THIS, not any provider.
 * Falls back to a plain neutral panel if no provider is registered — that
 * way the app never crashes when a provider's dynamic import fails.
 */
export default function Map(props) {
  const providerId = resolveMapProviderId(props.provider);
  const provider = providerId ? _registry.get(providerId) : null;
  if (!provider) {
    return (
      <div
        style={{ height: props.height || "100%", minHeight: 300 }}
        className="w-full h-full grid place-items-center bg-stone-100 text-stone-500 text-sm"
        data-testid="map-no-provider"
      >
        Map provider unavailable
      </div>
    );
  }
  const Renderer = provider.Component;
  return <Renderer {...props} data-provider={provider.id} />;
}

// Named export makes it easy to introspect the active provider (settings UI).
export { Map };
